
import React from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { StoreProvider } from './context/StoreContext';
import { Navbar } from './components/Navbar';
import { PendingRequestNotice } from './components/PendingRequestNotice';
import { BottomNavbar } from './components/BottomNavbar';
import { ContactPhonePrompt } from './components/ContactPhonePrompt';
import { AccessTutorial } from './components/AccessTutorial';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Auth } from './pages/Auth';
import { POS } from './pages/POS';
import { WaiterApp } from './pages/WaiterApp';
import { MenuManager } from './pages/MenuManager';
import { AdminSubscription } from './pages/AdminSubscription';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { Stock } from './pages/Stock';
import { Kitchen } from './pages/Kitchen';
import { DeliveryMenu } from './pages/DeliveryMenu';
import { DeliveryPanel } from './pages/DeliveryPanel';
import { DeliveryTrack } from './pages/DeliveryTrack';
import { ResetPassword } from './pages/ResetPassword';
import { BillingSuccess } from './pages/BillingSuccess';
import { AccessSelector } from './pages/AccessSelector';
import { UserRole } from './types';

const Layout = ({ children }: { children?: React.ReactNode }) => {
  const location = useLocation();
  const noNavRoutes = ['/', '/waiter', '/reset-password', '/billing/success'];
  const hideNavs = noNavRoutes.includes(location.pathname) || location.pathname.startsWith('/waiter') || location.pathname.startsWith('/delivery/menu') || location.pathname.startsWith('/delivery/track') || location.pathname.startsWith('/loja/');
  
  const showBottomNav = !hideNavs && !location.pathname.startsWith('/admin');

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {!hideNavs && <Navbar />}
      <main className={`flex-1 relative flex flex-col ${showBottomNav ? 'pb-20 md:pb-0' : ''} overflow-y-auto`}>
        {children}
      </main>
      {showBottomNav && <BottomNavbar />}
      {!hideNavs && <ContactPhonePrompt />}
      <AccessTutorial />
    </div>
  );
};

const App = () => {
  return (
    <StoreProvider>
      <Router>
        <PendingRequestNotice />
        <Layout>
          <Routes>
            <Route path="/" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/access" element={<ProtectedRoute allowedRoles={[UserRole.OWNER]}><AccessSelector /></ProtectedRoute>} />
            
            <Route 
              path="/pos" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER]}>
                  <POS />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/menu" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER]}>
                  <MenuManager />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/stock" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER]}>
                  <Stock />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/reports" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER]}>
                  <Reports />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/kitchen" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER]}>
                  <Kitchen />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/delivery" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER]}>
                  <DeliveryPanel />
                </ProtectedRoute>
              } 
            />

            <Route path="/delivery/menu" element={<DeliveryMenu />} />
            <Route path="/loja/:companyId" element={<DeliveryMenu />} />
            <Route path="/delivery/track/:orderId" element={<DeliveryTrack />} />

            <Route 
              path="/settings" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.CASHIER, UserRole.SAAS_ADMIN]}>
                  <Settings />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/waiter" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.WAITER, UserRole.OWNER]}>
                  <WaiterApp />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/admin" 
              element={
                <ProtectedRoute allowedRoles={[UserRole.SAAS_ADMIN]}>
                  <AdminSubscription />
                </ProtectedRoute>
              } 
            />
            
            <Route path="*" element={<Auth />} />
          </Routes>
        </Layout>
      </Router>
    </StoreProvider>
  );
};

export default App;
